import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import { useNewVersionAvailable } from '../lib/version';
import { useTranslation } from 'react-i18next';

/**
 * "There is a newer version of this page."
 *
 * Deliberately not an immediate reload: a reload throws away whatever is half
 * typed, and the tab is still working at this point. So it offers the reload,
 * and takes it itself at the next navigation — moving between screens is the
 * one moment nothing is half finished.
 */
export default function UpdateBanner() {
  const { t } = useTranslation();
  const stale = useNewVersionAvailable();
  const location = useLocation();
  const armedAt = useRef<string | null>(null);

  useEffect(() => {
    if (!stale) {
      armedAt.current = null;
      return;
    }
    // Remember where the notice appeared; reload on the first move away from it.
    if (armedAt.current === null) {
      armedAt.current = location.pathname;
      return;
    }
    if (location.pathname !== armedAt.current) window.location.reload();
  }, [stale, location.pathname]);

  if (!stale) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-sm bg-gray-900 text-white rounded-lg shadow-lg p-4 flex items-start gap-3">
      <RefreshCw className="h-5 w-5 mt-0.5 shrink-0" />
      <div className="text-sm">
        <p className="font-medium">{t('update.available')}</p>
        <p className="text-gray-300">
          {t('update.body')}
        </p>
        <button
          onClick={() => window.location.reload()}
          className="mt-2 px-3 py-1.5 bg-white text-gray-900 rounded font-medium hover:bg-gray-100"
        >
          {t('update.reload')}
        </button>
      </div>
    </div>
  );
}
